// src/lib/managers/dataManager/graph/sent-search-logic.ts

/**
 * Lecture du dossier Envoyés avec des critères (étape `lireMails`, boîte « envoyes », du contrat par
 * étapes de l'assistant). Un seul dossier, choisi par l'appelant : rien ici ne déduit un dossier des
 * autres critères, à la différence de la recherche des vues (`graph-search-logic.ts`).
 *
 * Deux lectures, selon les critères :
 *  - sans destinataire ni mot-clé : la liste du dossier, filtrée et triée par date (`$filter`), exacte ;
 *  - avec destinataire ou mots-clés : une recherche par terme dans le dossier (`$search`), dont les
 *    résultats sont réunis, puis filtrés ici (dates, pièce jointe, destinataire exact).
 *
 * Fonctions pures : les appels à Graph sont dans GraphManager.searchSentMailCopyRefs.
 */

import type { ViewFilter } from "../../customView";
import type { SentMailsCriteria } from "../IDataApi";
import {
    applyDatePostFilter,
    buildFilterParts,
    buildKeywordsSearchParts,
    consolidateSearchResults,
    encodeKqlSearchValue,
    type RawGraphItem,
} from "./graph-search-logic";
import { MailFolder } from "./types/graph";

/** Taille de page d'une recherche : fixée, la page par défaut de Graph est de 10 messages. */
export const SENT_SEARCH_PAGE_SIZE = 250;

/** Pages lues par terme, au plus : 1 000 résultats, le plafond d'une recherche `$search` de Graph. */
export const SENT_SEARCH_MAX_PAGES = 4;

const SENT_MESSAGES = `/me/mailFolders/${MailFolder.SentItems}/messages`;

/** Ce qu'il faut d'un message pour le retrouver (copie du dossier) et le filtrer ici. */
const SENT_SELECT =
    "id,internetMessageId,parentFolderId,sentDateTime,hasAttachments,toRecipients,ccRecipients,bccRecipients";

/** Vrai si la lecture passe par la recherche : un destinataire ou un mot-clé est demandé. */
export const hasSentSearchTerms = (criteria: SentMailsCriteria): boolean =>
    criteria.recipients.length > 0 || criteria.keywords.length > 0;

/** Liste du dossier Envoyés, filtrée (dates, pièce jointe) et triée par date d'envoi. */
export function sentFolderListUrl(
    criteria: SentMailsCriteria,
    limit: number,
): string {
    // `buildFilterParts` met toujours `sentDateTime` en tête du filtre : Graph exige que la propriété
    // triée y figure, et d'abord.
    const filterParts = buildFilterParts({
        startDate: criteria.startDate,
        endDate: criteria.endDate,
        hasAttachment: criteria.hasAttachment,
    } as ViewFilter);
    // Espaces littéraux, jamais `%20` (cf. GraphManager.searchInternetMessageIdsFromViewFilter).
    return (
        `${SENT_MESSAGES}?$select=${SENT_SELECT}` +
        `&$filter=${filterParts.join(" and ")}` +
        `&$top=${limit}` +
        `&$orderby=sentDateTime ${criteria.sortOrder}`
    );
}

/** Une recherche par terme dans le dossier Envoyés : par destinataire, par mot-clé. */
export function sentFolderSearchUrls(criteria: SentMailsCriteria): {
    recipients: string[];
    keywords: string[];
} {
    const url = (kql: string) =>
        `${SENT_MESSAGES}?$select=${SENT_SELECT}` +
        `&$top=${SENT_SEARCH_PAGE_SIZE}` +
        `&$search=${encodeKqlSearchValue(kql)}`;
    return {
        recipients: criteria.recipients.map((recipient) =>
            url(`"recipients:${recipient.replace(/"/g, '\\"')}"`),
        ),
        keywords: buildKeywordsSearchParts(criteria.keywords).map(url),
    };
}

/**
 * Vrai si une page de recherche, triée du plus récent au plus ancien, a dépassé le début de la
 * période : les pages suivantes ne contiennent plus que des mails trop anciens. Une page qui n'est
 * pas dans cet ordre ne permet pas de le dire : faux.
 */
export function isPastStart(
    page: RawGraphItem[],
    startDate: string | null,
): boolean {
    if (!startDate || page.length === 0) return false;
    const start = new Date(startDate).getTime();
    const first = new Date(page[0].sentDateTime).getTime();
    const last = new Date(page[page.length - 1].sentDateTime).getTime();
    if ([start, first, last].some(Number.isNaN)) return false;
    return first >= last && last < start;
}

type RawRecipient = { emailAddress?: { address?: string; name?: string } };

/**
 * Vrai si le mail est adressé à l'un des destinataires demandés (à, cc ou cci). Une adresse demandée
 * doit s'y trouver telle quelle ; un nom que les contacts n'ont pas résolu se cherche dans les adresses
 * et les noms. Un mail dont Graph ne rend aucune adresse lisible est gardé : la recherche l'a trouvé.
 */
export function matchesRecipients(
    item: RawGraphItem,
    recipients: string[],
): boolean {
    const dests: RawRecipient[] = [
        ...(item.toRecipients ?? []),
        ...(item.ccRecipients ?? []),
        ...(item.bccRecipients ?? []),
    ];
    const addresses = dests
        .map((dest) => dest.emailAddress?.address?.toLowerCase() ?? "")
        .filter((address) => address.includes("@"));
    if (addresses.length === 0) return true;
    const names = dests.map(
        (dest) => dest.emailAddress?.name?.toLowerCase() ?? "",
    );
    return recipients.some((recipient) => {
        const wanted = recipient.toLowerCase();
        return wanted.includes("@")
            ? addresses.includes(wanted)
            : [...addresses, ...names].some((text) => text.includes(wanted));
    });
}

const uniqueByMessage = (items: RawGraphItem[]): RawGraphItem[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
        if (seen.has(item.internetMessageId)) return false;
        seen.add(item.internetMessageId);
        return true;
    });
};

/**
 * Les mails rendus par une recherche dans les Envoyés : ceux qui répondent à tous les critères
 * (un destinataire parmi ceux demandés ET un mot-clé parmi ceux demandés, dans la période, avec ou
 * sans pièce jointe), triés par date d'envoi, `limit` au plus.
 */
export function selectSentResults(
    recipientsResults: RawGraphItem[],
    keywordsResults: RawGraphItem[],
    criteria: SentMailsCriteria,
    limit: number,
): RawGraphItem[] {
    let results = consolidateSearchResults(
        uniqueByMessage(keywordsResults),
        uniqueByMessage(recipientsResults),
        criteria.keywords.length > 0,
        criteria.recipients.length > 0,
    ).results;
    // `$search` ne se combine pas à `$filter` : dates et pièce jointe se filtrent ici.
    results = applyDatePostFilter(
        results,
        criteria.startDate,
        criteria.endDate,
    );
    if (criteria.hasAttachment !== undefined) {
        results = results.filter(
            (item) => Boolean(item.hasAttachments) === criteria.hasAttachment,
        );
    }
    if (criteria.recipients.length > 0) {
        results = results.filter((item) =>
            matchesRecipients(item, criteria.recipients),
        );
    }
    const direction = criteria.sortOrder === "ASC" ? 1 : -1;
    return [...results]
        .sort(
            (a, b) =>
                direction *
                (new Date(a.sentDateTime).getTime() -
                    new Date(b.sentDateTime).getTime()),
        )
        .slice(0, limit);
}
